import axios, { AxiosError } from "axios"
import { Dispatch } from "redux"

/* eslint-disable camelcase */

export const enum MyUnisoftCabinetActionsEnum {
  GET_CONNECTIONS_ATTEMPT = "MY_UNISOFT_CABINET/getConnectionsAttempt",
  GET_CONNECTIONS_SUCCESS = "MY_UNISOFT_CABINET/getConnectionsSuccess",
  GET_CONNECTIONS_FAILURE = "MY_UNISOFT_CABINET/getConnectionsFailure",
  CONNECT_ATTEMPT = "MY_UNISOFT_CABINET/connectAttempt",
  CONNECT_SUCCESS = "MY_UNISOFT_CABINET/connectSuccess",
  CONNECT_FAILURE = "MY_UNISOFT_CABINET/connectFailure",
  GET_OVERVIEW_ATTEMPT = "MY_UNISOFT_CABINET/getOverviewAttempt",
  GET_OVERVIEW_SUCCESS = "MY_UNISOFT_CABINET/getOverviewSuccess",
  GET_OVERVIEW_FAILURE = "MY_UNISOFT_CABINET/getOverviewFailure",
  RELAUNCH_ATTEMPT = "MY_UNISOFT_CABINET/relaunchAttempt",
  RELAUNCH_SUCCESS = "MY_UNISOFT_CABINET/relaunchSuccess",
  RELAUNCH_FAILURE = "MY_UNISOFT_CABINET/relaunchFailure",
  DISCONNECT_ATTEMPT = "MY_UNISOFT_CABINET/disconnectAttempt",
  DISCONNECT_SUCCESS = "MY_UNISOFT_CABINET/disconnectSuccess",
  DISCONNECT_FAILURE = "MY_UNISOFT_CABINET/disconnectFailure",
  RESET_STATUS = "MY_UNISOFT_CABINET/resetStatus",
}

export type MyUnisoftCabinetStatus = "IDLE" | "PENDING" | "SUCCESS" | "ERROR"

export interface MyUnisoftCabinetCompany {
  id: number
  name: string
  identification_number: string | null
  accounting_software: string | null
  my_unisoft_company_id: string | null
  my_unisoft_api_connection_id: number | null
}

export interface MyUnisoftCabinetConnection {
  id: number
  fiduciary_id: number
  schema_id: number
  third_party_id: number | null
  label: string | null
  last_checked_at: string | null
  created_at: string
  updated_at: string
  companies: MyUnisoftCabinetCompany[]
}

export interface MyUnisoftRemoteCompany {
  my_unisoft_company_id: string
  corporate_name: string | null
  siret: string | null
  folder_reference: string | null
  associated_in_chaintrust: boolean
  chaintrust_company_id: number | null
}

export interface MyUnisoftAutoAssociation {
  associated: Array<{ company_id: number; my_unisoft_company_id: string }>
  updated: Array<{ company_id: number; my_unisoft_company_id: string }>
  ambiguous: Array<{ company_id: number; my_unisoft_company_ids: number[] }>
  not_found: number
}

export interface MyUnisoftCabinetIndexPayload {
  connections: MyUnisoftCabinetConnection[]
  available_companies: MyUnisoftCabinetCompany[]
}

export const getMyUnisoftCabinetConnectionsAttemptAction = () =>
  ({ type: MyUnisoftCabinetActionsEnum.GET_CONNECTIONS_ATTEMPT } as const)

export const getMyUnisoftCabinetConnectionsSuccessAction = (
  payload: MyUnisoftCabinetIndexPayload
) =>
  ({
    type: MyUnisoftCabinetActionsEnum.GET_CONNECTIONS_SUCCESS,
    payload,
  } as const)

export const getMyUnisoftCabinetConnectionsFailureAction = () =>
  ({ type: MyUnisoftCabinetActionsEnum.GET_CONNECTIONS_FAILURE } as const)

export const connectMyUnisoftCabinetAttemptAction = () =>
  ({ type: MyUnisoftCabinetActionsEnum.CONNECT_ATTEMPT } as const)

export const connectMyUnisoftCabinetSuccessAction = (
  autoAssociation: MyUnisoftAutoAssociation | null
) =>
  ({
    type: MyUnisoftCabinetActionsEnum.CONNECT_SUCCESS,
    payload: autoAssociation,
  } as const)

export const connectMyUnisoftCabinetFailureAction = (errorMessage: string) =>
  ({
    type: MyUnisoftCabinetActionsEnum.CONNECT_FAILURE,
    payload: errorMessage,
  } as const)

export interface MyUnisoftCabinetOverview {
  connected: MyUnisoftCabinetCompany[]
  available: MyUnisoftRemoteCompany[]
}

export const getMyUnisoftCabinetOverviewAttemptAction = (
  connectionId: number
) =>
  ({
    type: MyUnisoftCabinetActionsEnum.GET_OVERVIEW_ATTEMPT,
    payload: connectionId,
  } as const)

export const getMyUnisoftCabinetOverviewSuccessAction = (
  overview: MyUnisoftCabinetOverview
) =>
  ({
    type: MyUnisoftCabinetActionsEnum.GET_OVERVIEW_SUCCESS,
    payload: overview,
  } as const)

export const getMyUnisoftCabinetOverviewFailureAction = (
  errorMessage: string
) =>
  ({
    type: MyUnisoftCabinetActionsEnum.GET_OVERVIEW_FAILURE,
    payload: errorMessage,
  } as const)

export const relaunchMyUnisoftCabinetAttemptAction = (connectionId: number) =>
  ({
    type: MyUnisoftCabinetActionsEnum.RELAUNCH_ATTEMPT,
    payload: connectionId,
  } as const)

export const relaunchMyUnisoftCabinetSuccessAction = (
  autoAssociation: MyUnisoftAutoAssociation | null
) =>
  ({
    type: MyUnisoftCabinetActionsEnum.RELAUNCH_SUCCESS,
    payload: autoAssociation,
  } as const)

export const relaunchMyUnisoftCabinetFailureAction = (errorMessage: string) =>
  ({
    type: MyUnisoftCabinetActionsEnum.RELAUNCH_FAILURE,
    payload: errorMessage,
  } as const)

export const disconnectMyUnisoftCabinetAttemptAction = () =>
  ({ type: MyUnisoftCabinetActionsEnum.DISCONNECT_ATTEMPT } as const)

export const disconnectMyUnisoftCabinetSuccessAction = () =>
  ({ type: MyUnisoftCabinetActionsEnum.DISCONNECT_SUCCESS } as const)

export const disconnectMyUnisoftCabinetFailureAction = (errorMessage: string) =>
  ({
    type: MyUnisoftCabinetActionsEnum.DISCONNECT_FAILURE,
    payload: errorMessage,
  } as const)

// Chaque modale ne remet à zéro que son opération : une autre peut être en vol.
export type MyUnisoftCabinetScope = "connect" | "overview" | "disconnect"

export const resetMyUnisoftCabinetStatusAction = (
  scope: MyUnisoftCabinetScope
) =>
  ({ type: MyUnisoftCabinetActionsEnum.RESET_STATUS, payload: scope } as const)

export type MyUnisoftCabinetActionsType = ReturnType<
  | typeof getMyUnisoftCabinetConnectionsAttemptAction
  | typeof getMyUnisoftCabinetConnectionsSuccessAction
  | typeof getMyUnisoftCabinetConnectionsFailureAction
  | typeof connectMyUnisoftCabinetAttemptAction
  | typeof connectMyUnisoftCabinetSuccessAction
  | typeof connectMyUnisoftCabinetFailureAction
  | typeof getMyUnisoftCabinetOverviewAttemptAction
  | typeof getMyUnisoftCabinetOverviewSuccessAction
  | typeof getMyUnisoftCabinetOverviewFailureAction
  | typeof relaunchMyUnisoftCabinetAttemptAction
  | typeof relaunchMyUnisoftCabinetSuccessAction
  | typeof relaunchMyUnisoftCabinetFailureAction
  | typeof disconnectMyUnisoftCabinetAttemptAction
  | typeof disconnectMyUnisoftCabinetSuccessAction
  | typeof disconnectMyUnisoftCabinetFailureAction
  | typeof resetMyUnisoftCabinetStatusAction
>

export interface MyUnisoftCabinetState {
  connections: MyUnisoftCabinetConnection[]
  availableCompanies: MyUnisoftCabinetCompany[]
  connectionsStatus: MyUnisoftCabinetStatus
  connectStatus: MyUnisoftCabinetStatus
  errorMessage: string | null
  lastAutoAssociation: MyUnisoftAutoAssociation | null
  overview: MyUnisoftCabinetOverview | null
  overviewConnectionId: number | null
  // Distinct de overviewConnectionId : les deux opérations sont indépendantes.
  relaunchConnectionId: number | null
  overviewStatus: MyUnisoftCabinetStatus
  relaunchStatus: MyUnisoftCabinetStatus
  disconnectStatus: MyUnisoftCabinetStatus
}

export const myUnisoftCabinetInitialState: MyUnisoftCabinetState = {
  connections: [],
  availableCompanies: [],
  connectionsStatus: "IDLE",
  connectStatus: "IDLE",
  errorMessage: null,
  lastAutoAssociation: null,
  overview: null,
  overviewConnectionId: null,
  relaunchConnectionId: null,
  overviewStatus: "IDLE",
  relaunchStatus: "IDLE",
  disconnectStatus: "IDLE",
}

export function myUnisoftCabinetReducer(
  state = myUnisoftCabinetInitialState,
  action: MyUnisoftCabinetActionsType
): MyUnisoftCabinetState {
  switch (action.type) {
    case MyUnisoftCabinetActionsEnum.GET_CONNECTIONS_ATTEMPT:
      return { ...state, connectionsStatus: "PENDING" }
    case MyUnisoftCabinetActionsEnum.GET_CONNECTIONS_SUCCESS:
      return {
        ...state,
        connectionsStatus: "SUCCESS",
        connections: action.payload.connections || [],
        availableCompanies: action.payload.available_companies || [],
      }
    case MyUnisoftCabinetActionsEnum.GET_CONNECTIONS_FAILURE:
      return { ...state, connectionsStatus: "ERROR" }
    case MyUnisoftCabinetActionsEnum.CONNECT_ATTEMPT:
      return {
        ...state,
        connectStatus: "PENDING",
        errorMessage: null,
        lastAutoAssociation: null,
      }
    case MyUnisoftCabinetActionsEnum.CONNECT_SUCCESS:
      return {
        ...state,
        connectStatus: "SUCCESS",
        errorMessage: null,
        lastAutoAssociation: action.payload,
      }
    case MyUnisoftCabinetActionsEnum.CONNECT_FAILURE:
      return { ...state, connectStatus: "ERROR", errorMessage: action.payload }
    case MyUnisoftCabinetActionsEnum.GET_OVERVIEW_ATTEMPT:
      return {
        ...state,
        overviewStatus: "PENDING",
        overview: null,
        overviewConnectionId: action.payload,
        errorMessage: null,
      }
    case MyUnisoftCabinetActionsEnum.GET_OVERVIEW_SUCCESS:
      return {
        ...state,
        overviewStatus: "SUCCESS",
        overview: action.payload,
        errorMessage: null,
      }
    case MyUnisoftCabinetActionsEnum.GET_OVERVIEW_FAILURE:
      return {
        ...state,
        overviewStatus: "ERROR",
        overview: null,
        errorMessage: action.payload,
      }
    case MyUnisoftCabinetActionsEnum.DISCONNECT_ATTEMPT:
      return {
        ...state,
        disconnectStatus: "PENDING",
        errorMessage: null,
        lastAutoAssociation: null,
      }
    case MyUnisoftCabinetActionsEnum.DISCONNECT_SUCCESS:
      return { ...state, disconnectStatus: "SUCCESS", errorMessage: null }
    case MyUnisoftCabinetActionsEnum.RELAUNCH_ATTEMPT:
      return {
        ...state,
        relaunchStatus: "PENDING",
        relaunchConnectionId: action.payload,
        errorMessage: null,
      }
    case MyUnisoftCabinetActionsEnum.RELAUNCH_SUCCESS:
      return {
        ...state,
        relaunchStatus: "SUCCESS",
        lastAutoAssociation: action.payload,
        errorMessage: null,
      }
    case MyUnisoftCabinetActionsEnum.RELAUNCH_FAILURE:
      return {
        ...state,
        relaunchStatus: "ERROR",
        errorMessage: action.payload,
      }
    case MyUnisoftCabinetActionsEnum.DISCONNECT_FAILURE:
      return {
        ...state,
        disconnectStatus: "ERROR",
        errorMessage: action.payload,
      }
    case MyUnisoftCabinetActionsEnum.RESET_STATUS: {
      const statusKey = {
        connect: "connectStatus",
        overview: "overviewStatus",
        disconnect: "disconnectStatus",
      }[action.payload] as
        | "connectStatus"
        | "overviewStatus"
        | "disconnectStatus"

      return { ...state, [statusKey]: "IDLE", errorMessage: null }
    }
    default:
      return { ...state }
  }
}

const BASE_URL = "my_unisoft_cabinet_connections"

export const myUnisoftCabinetErrorMessage = (error: AxiosError): string => {
  const data = error.response?.data as
    | { message?: string; error?: string }
    | undefined
  return data?.message || data?.error || "unknown_error"
}

export const getMyUnisoftCabinetConnectionsThunk =
  () => (dispatch: Dispatch<MyUnisoftCabinetActionsType>) => {
    dispatch(getMyUnisoftCabinetConnectionsAttemptAction())
    return axios
      .get<MyUnisoftCabinetIndexPayload>(`/${BASE_URL}`)
      .then((response) =>
        dispatch(getMyUnisoftCabinetConnectionsSuccessAction(response.data))
      )
      .catch(() => dispatch(getMyUnisoftCabinetConnectionsFailureAction()))
  }

export const connectMyUnisoftCabinetThunk =
  (token: string, label?: string) =>
  async (dispatch: Dispatch<MyUnisoftCabinetActionsType>) => {
    dispatch(connectMyUnisoftCabinetAttemptAction())
    try {
      const response = await axios.post<{
        auto_association?: MyUnisoftAutoAssociation
      }>(`/${BASE_URL}/connect`, { token, label })
      dispatch(
        connectMyUnisoftCabinetSuccessAction(
          response.data?.auto_association || null
        )
      )
      return getMyUnisoftCabinetConnectionsThunk()(dispatch)
    } catch (error) {
      return dispatch(
        connectMyUnisoftCabinetFailureAction(
          myUnisoftCabinetErrorMessage(error as AxiosError)
        )
      )
    }
  }

export const disconnectMyUnisoftCabinetConnectionThunk =
  (connectionId: number) =>
  async (dispatch: Dispatch<MyUnisoftCabinetActionsType>) => {
    dispatch(disconnectMyUnisoftCabinetAttemptAction())
    try {
      await axios.delete(`/${BASE_URL}/${connectionId}/disconnect`)
      dispatch(disconnectMyUnisoftCabinetSuccessAction())
      return getMyUnisoftCabinetConnectionsThunk()(dispatch)
    } catch (error) {
      return dispatch(
        disconnectMyUnisoftCabinetFailureAction(
          myUnisoftCabinetErrorMessage(error as AxiosError)
        )
      )
    }
  }

export const getMyUnisoftCabinetOverviewThunk =
  (connectionId: number) =>
  async (dispatch: Dispatch<MyUnisoftCabinetActionsType>) => {
    dispatch(getMyUnisoftCabinetOverviewAttemptAction(connectionId))
    try {
      const [connected, available] = await Promise.all([
        axios.get<{ companies: MyUnisoftCabinetCompany[] }>(
          `/${BASE_URL}/${connectionId}/connected_companies`
        ),
        axios.get<{ companies: MyUnisoftRemoteCompany[] }>(
          `/${BASE_URL}/${connectionId}/available_companies`
        ),
      ])
      return dispatch(
        getMyUnisoftCabinetOverviewSuccessAction({
          connected: connected.data.companies || [],
          available: available.data.companies || [],
        })
      )
    } catch (error) {
      return dispatch(
        getMyUnisoftCabinetOverviewFailureAction(
          myUnisoftCabinetErrorMessage(error as AxiosError)
        )
      )
    }
  }

export const relaunchMyUnisoftCabinetConnectionThunk =
  (connectionId: number) =>
  async (dispatch: Dispatch<MyUnisoftCabinetActionsType>) => {
    dispatch(relaunchMyUnisoftCabinetAttemptAction(connectionId))
    try {
      const response = await axios.post<{
        auto_association?: MyUnisoftAutoAssociation
      }>(`/${BASE_URL}/${connectionId}/relaunch`)
      dispatch(
        relaunchMyUnisoftCabinetSuccessAction(
          response.data?.auto_association || null
        )
      )
      return getMyUnisoftCabinetConnectionsThunk()(dispatch)
    } catch (error) {
      return dispatch(
        relaunchMyUnisoftCabinetFailureAction(
          myUnisoftCabinetErrorMessage(error as AxiosError)
        )
      )
    }
  }
